"use client";

import For from "@/components/common/For";
import If from "@/components/common/If";
import * as Accordion from "@radix-ui/react-accordion";
import { Card } from "@/components/ui/Card";
import { ChevronLeft, ChevronRight, Plus, X, Search } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { searchResourcesDirect } from "@/actions/resource-search";
import { useLenis } from "lenis/react";

const DOCUMENT_TYPE_CATEGORY = "Document Type";
const DEFAULT_OPEN_CATEGORIES = ["Document Type", "Series"];

// All available tags for autocomplete suggestions
const getAllTags = (contentfulTags: string[]) =>
  Array.from(new Set(contentfulTags)).sort();

const buildTagMaps = (menu: any[]) => {
  const tagToKey: Record<string, string> = {};
  const keyToTag: Record<string, string> = {};
  for (const item of menu) {
    for (const sub of item?.subMenuItemsCollection?.items || []) {
      const title = sub?.title?.trim();
      const key = sub?.alternateLink?.trim();
      if (title && key) {
        tagToKey[title] = key;
        keyToTag[key] = title;
      }
    }
  }
  return { tagToKey, keyToTag };
};

const languageTags = ["English", "French"];

// Helper function to normalize strings for matching by removing special characters
const normalizeForMatch = (str: string) => {
  return str.toLowerCase().replace(/[®™©]/g, "").trim();
};

interface Resource {
  sys: {
    id: string;
  };
  title: string;
  line2?: string;
  line3?: string;
  documentLink?: string;
  documentCode?: string;
  type?: string;
  resource?: Array<{
    cdn_url: string;
  }>;
  thumbnail?: {
    url: string;
    width: number;
    height: number;
    description?: string;
  };
  contentfulMetadata?: {
    tags: Array<{
      id: string;
      name: string;
    }>;
  };
}

const getResourceTags = (item: Resource) =>
  item.contentfulMetadata?.tags?.map((tag: any) => tag.name) || [];

const matchesFilter = (item: Resource, filter: string) => {
  const tags = getResourceTags(item);
  const filterNormalized = normalizeForMatch(filter);

  // Debug logging for Security / Attack Resistant
  if (filter === "Security / Attack Resistant") {
    console.log("Security / Attack Resistant filter:", {
      filter,
      filterNormalized,
      tags,
      hasMatch: tags.some((tag) => normalizeForMatch(tag) === filterNormalized),
    });
  }

  if (filter === "English") {
    const hasEnglish = tags.includes("English");
    const hasAnyLanguage = languageTags.some((lang) => tags.includes(lang));
    return hasEnglish || !hasAnyLanguage;
  }

  return tags.some((tag) => normalizeForMatch(tag) === filterNormalized);
};

const matchesAllFilters = (item: Resource, filters: string[]) => {
  const languageFilters = filters.filter((filter) =>
    languageTags.includes(filter),
  );
  const nonLanguageFilters = filters.filter(
    (filter) => !languageTags.includes(filter),
  );

  const matchesNonLanguage = nonLanguageFilters.every((filter) =>
    matchesFilter(item, filter),
  );

  const matchesLanguage =
    languageFilters.length === 0 ||
    languageFilters.some((filter) => matchesFilter(item, filter));

  return matchesNonLanguage && matchesLanguage;
};

const buildCategoriesFromMenu = (menu: any[]) => {
  const result: Record<string, string[]> = {};
  for (const item of menu) {
    if (item?.title) {
      result[item.title] =
        item.subMenuItemsCollection?.items
          ?.map((sub: any) => sub?.title?.trim())
          .filter(Boolean) || [];
    }
  }
  return result;
};

export default function UI({
  resources = [],
  contentfulTags = [],
  resourceMenu = [],
}: {
  resources: Resource[];
  contentfulTags?: string[];
  resourceMenu?: any[];
}) {
  const categories = useMemo(
    () => buildCategoriesFromMenu(resourceMenu),
    [resourceMenu],
  );

  const documentTypes = useMemo(
    () => categories[DOCUMENT_TYPE_CATEGORY] ?? [],
    [categories],
  );

  const { tagToKey, keyToTag } = useMemo(
    () => buildTagMaps(resourceMenu),
    [resourceMenu],
  );

  const tagToSlug = useCallback(
    (tag: string) =>
      tagToKey[tag] ??
      encodeURIComponent(tag.replace(/\s+/g, "-").toLowerCase()),
    [tagToKey],
  );

  const slugToTag = useCallback(
    (slug: string) =>
      keyToTag[slug.trim()] ??
      decodeURIComponent(slug.trim()).replace(/-/g, " "),
    [keyToTag],
  );
  const query = useSearchParams();
  const router = useRouter();
  const lenis = useLenis();
  const [search, setSearch] = useState("");
  const [activeSearch, setActiveSearch] = useState(""); // The search query that's actually being executed
  const [filters, setFilters] = useState<string[]>([]);
  const [results, setResults] = useState(resources);
  const pageSize = 60;
  const [page, setPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [semanticResults, setSemanticResults] = useState<
    Array<{ contentfulEntryId: string; score: number }>
  >([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Build all available tags from hardcoded + Contentful tags
  const allTags = useMemo(() => getAllTags(contentfulTags), [contentfulTags]);

  // Filter tags based on search input for autocomplete suggestions
  const suggestedTags = useMemo(() => {
    if (!search || search.length < 2) return [];

    const searchNormalized = normalizeForMatch(search);
    return allTags
      .filter(
        (tag) =>
          normalizeForMatch(tag).includes(searchNormalized) &&
          !filters.includes(tag), // Don't suggest already-selected tags
      )
      .slice(0, 5); // Limit to 5 suggestions
  }, [search, filters]);

  // Handle clicking a tag suggestion
  const handleTagSuggestionClick = (tag: string) => {
    // Add the tag as a filter
    const newFilters = [...filters, tag];
    setFilters(newFilters);

    // Update URL - use reverse mapping if exists, otherwise use raw tag
    const queryTags = newFilters.map(tagToSlug).join(",");
    const searchParams = new URLSearchParams();
    searchParams.set("tags", queryTags || "all");
    router.push(`?${searchParams.toString()}`);

    // Clear search input and hide suggestions
    setSearch("");
    setShowTagSuggestions(false);
  };

  // Semantic search function - memoized to prevent infinite re-renders
  const performSearch = useCallback(
    async (query: string, tagFilters: string[]) => {
      if (query.length < 2) {
        setSemanticResults([]);
        setActiveSearch("");
        return;
      }

      setIsSearching(true);
      setActiveSearch(query);

      try {
        const searchResults = await searchResourcesDirect(
          query,
          tagFilters,
          50,
        );

        // Extract contentfulEntryIds from results
        const processed: Array<{ contentfulEntryId: string; score: number }> =
          [];

        for (const r of searchResults) {
          const entryId = r.metadata.contentfulEntryId;
          const docId = r.metadata.documentId;

          // Use contentfulEntryId if available, otherwise try to extract from documentId (format: resource-{sys.id})
          const fallbackId =
            entryId ||
            (docId?.startsWith("resource-")
              ? docId.replace("resource-", "")
              : null);

          if (fallbackId) {
            processed.push({ contentfulEntryId: fallbackId, score: r.score });
          }
        }

        setSemanticResults(processed);
      } catch (error) {
        console.error("Search error:", error);
        setSemanticResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [],
  );

  // Build a set of semantic search IDs for quick lookup
  const semanticSearchIds = useMemo(
    () => new Set(semanticResults.map((r) => r.contentfulEntryId)),
    [semanticResults],
  );

  const filterResults = useCallback(
    (
      data: Resource[],
      currentSearch: string,
      currentFilters: string[],
      semanticIds: Set<string> | null,
    ) => {
      // Check if search query matches any exact tag names
      const searchLower = currentSearch?.toLowerCase();
      const searchNormalized = searchLower
        ? normalizeForMatch(searchLower)
        : "";
      const matchingTags = searchNormalized
        ? allTags.filter((tag) => normalizeForMatch(tag) === searchNormalized)
        : [];

      // Get resources that have the matching tags
      const tagMatchResources =
        matchingTags.length > 0
          ? data.filter((item: Resource) => {
              const resourceTags = getResourceTags(item);
              return matchingTags.some((tag: string) =>
                resourceTags.includes(tag),
              );
            })
          : [];

      // If semantic search results are available, use those
      let semanticResources: Resource[] = [];
      if (semanticIds && semanticIds.size > 0 && currentSearch) {
        semanticResources = data.filter((item: Resource) => {
          const matchesSemantic = semanticIds.has(item.sys?.id);
          const matchesFilters =
            currentFilters.length === 0 ||
            matchesAllFilters(item, currentFilters);
          return matchesSemantic && matchesFilters;
        });
      }

      // Combine results: tag matches first, then semantic results
      let combinedResults = [...tagMatchResources];

      // Add semantic results that aren't already in tag matches
      const tagMatchIds = new Set(
        tagMatchResources.map((item) => item.sys?.id),
      );
      const uniqueSemanticResults = semanticResources.filter(
        (item) => !tagMatchIds.has(item.sys?.id),
      );
      combinedResults = [...combinedResults, ...uniqueSemanticResults];

      // If we have tag matches or semantic results, return those
      if (
        matchingTags.length > 0 ||
        (semanticIds && semanticIds.size > 0 && currentSearch)
      ) {
        // Apply additional filters if needed
        if (currentFilters.length > 0) {
          combinedResults = combinedResults.filter((item: Resource) => {
            return matchesAllFilters(item, currentFilters);
          });
        }
        return combinedResults;
      }

      // Fall back to basic filtering
      if (!searchLower && !currentFilters?.length) {
        return [...data];
      }

      let filteredData = [...data];

      if (searchLower) {
        filteredData = filteredData.filter((item: Resource) =>
          normalizeForMatch(item?.title || "").includes(searchNormalized),
        );
      }

      if (currentFilters.length > 0) {
        filteredData = filteredData.filter((item: Resource) => {
          return matchesAllFilters(item, currentFilters);
        });
      }

      return filteredData;
    },
    [],
  );

  // Apply filters based on URL params
  useEffect(() => {
    const tags = query.get("tags");
    const newFilters =
      !tags || tags === "all"
        ? []
        : tags.split(",").map(slugToTag).filter(Boolean);

    setFilters(newFilters);
  }, [query, slugToTag]);

  // Update results when activeSearch, filters, or semantic search results change
  useEffect(() => {
    const shouldUseSemantic =
      semanticSearchIds.size > 0 && activeSearch.length >= 2;
    const filtered = filterResults(
      resources,
      activeSearch,
      filters,
      shouldUseSemantic ? semanticSearchIds : null,
    );
    setResults(filtered);
  }, [activeSearch, filters, semanticSearchIds, resources, filterResults]);

  useEffect(() => {
    setPage(1);
  }, [activeSearch, filters]);

  const totalPages = Math.max(1, Math.ceil(results.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedResults = results.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const resultsTopRef = useRef<HTMLDivElement | null>(null);

  const scrollResultsToTop = useCallback(() => {
    const el = resultsTopRef.current;
    if (!el) return;

    if (lenis) {
      lenis.scrollTo(el, { immediate: true });
      return;
    }

    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [lenis]);

  const goToPage = useCallback(
    (nextPage: number) => {
      const clamped = Math.min(Math.max(1, nextPage), totalPages);
      setPage(clamped);
      scrollResultsToTop();
    },
    [scrollResultsToTop, totalPages],
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    performSearch(search, filters);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleFilter = useCallback(
    (filter: string) => {
      const hasFilter = filters.includes(filter);
      let newFilters = hasFilter
        ? filters.filter((item) => item !== filter)
        : [...filters, filter];

      if (documentTypes.includes(filter)) {
        newFilters = newFilters.filter(
          (item) => !documentTypes.includes(item) || item === filter,
        );
      }

      const queryTags =
        newFilters.length === 0 ? "all" : newFilters.map(tagToSlug).join(",");

      const searchParams = new URLSearchParams();
      searchParams.set("tags", queryTags);
      router.push(`?${searchParams.toString()}`);
    },
    [filters, router, tagToSlug, documentTypes],
  );

  const formatTitle = (title) => {
    const titleArray = title.split("_");
    const id = titleArray.splice(0, 1);
    const theRest = titleArray.join("_");
    return `${id} ${theRest?.replace(/_/g, " ")?.replace(/-/g, " ")?.replace(".pdf", "")}`;
  };
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof ref.current?.classList === "undefined") return;

    if (window.innerWidth < 768) {
      ref.current.dataset.lenisPrevent = "true";
    } else {
      delete ref.current.dataset.lenisPrevent;
    }
  }, [isOpen]);

  return (
    <div className="resource-grid px-gutter min-h-[90vh] mb-20">
      <button onClick={() => setIsOpen(!isOpen)} className="flex md:hidden">
        Filters <ChevronRight />
      </button>
      <div className={`filters stack gap-6 ${isOpen ? "open" : ""}`} ref={ref}>
        <button onClick={() => setIsOpen(false)} className="flex md:hidden">
          <ChevronLeft />
          Close
        </button>
        <header className="stack gap-2 bg-[#F2F2F2]   p-4">
          <div className="relative">
            <input
              ref={searchInputRef}
              placeholder="Search"
              onChange={handleSearch}
              value={search}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowTagSuggestions(true)}
              onBlur={() => {
                // Delay hiding to allow click on suggestions
                setTimeout(() => setShowTagSuggestions(false), 150);
              }}
              className="w-full pr-10"
            />
            <button
              onClick={handleSearchSubmit}
              disabled={isSearching}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded disabled:opacity-50"
              aria-label="Search"
            >
              <Search size={18} />
            </button>

            {/* Tag suggestions dropdown */}
            {showTagSuggestions && suggestedTags.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                <div className="px-3 py-3 text-xs uppercase font-bold bg-black text-white text-gray-500 border-b border-gray-100">
                  Available tags
                </div>
                {suggestedTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleTagSuggestionClick(tag)}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-light-gray flex items-center gap-2"
                  >
                    <Plus size={14} className="text-gray-400" />
                    <span>{tag}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Show reset button when search is active but no filters */}
          {activeSearch && filters.length === 0 && (
            <button
              onClick={() => {
                setSearch("");
                setActiveSearch("");
                setSemanticResults([]);
              }}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Clear search
            </button>
          )}

          <div className="flex gap-2 flex-wrap">
            {filters.map((filter) => {
              return (
                <button
                  key={filter}
                  onClick={() => {
                    handleFilter(filter);
                  }}
                  className="border border-gold text-gold px-2 py-1 text-sm flex gap-2  items-center  "
                >
                  {filter} <X size={12} />
                </button>
              );
            })}
          </div>
          <div className="flex gap-4 justify-between">
            <If condition={filters?.length}>
              <button
                onClick={() => setFilters([])}
                className="flex gap-1 text-gray"
              >
                {/* <X /> */}
                Reset
              </button>
            </If>

            <span className="font-bold">
              {results?.length !== 1
                ? `${results?.length} documents`
                : "1 document"}
            </span>
          </div>
        </header>

        <For
          each={Object.entries(categories)}
          render={([category, items]) => {
            return (
              <Details
                title={category}
                key={category}
                open={DEFAULT_OPEN_CATEGORIES.includes(category)}
              >
                <ul className="stack gap-4 px-4">
                  <For
                    each={items}
                    render={(item) => (
                      <li key={item}>
                        <label className="flex gap-2 items-center leading-none">
                          <input
                            type="checkbox"
                            className="text-gold"
                            checked={filters?.includes(item)}
                            onChange={() => handleFilter(item)}
                          />
                          {item}
                        </label>
                      </li>
                    )}
                  />
                </ul>
              </Details>
            );
          }}
        />
      </div>

      <div>
        {/* Loading state */}
        <If condition={isSearching}>
          <div className="flex flex-col items-center justify-center py-20 text-gray-600">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mb-4"></div>
            <p>Searching...</p>
          </div>
        </If>

        {/* No results state (only show when not searching) */}
        <If condition={!isSearching && !results?.length}>
          <div className="py-20 text-center text-gray-600">
            <p className="text-lg mb-2">No documents found</p>
            {(activeSearch || filters.length > 0) && (
              <p className="text-sm">Try adjusting your search or filters</p>
            )}
          </div>
        </If>

        {/* Results */}
        <If condition={!isSearching && results?.length}>
          <div ref={resultsTopRef} />
          <div className="flex items-center justify-between pb-6">
            <div className="text-sm text-gray-600"></div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Page</span>
                <select
                  value={currentPage}
                  onChange={(e) => goToPage(Number(e.target.value))}
                  className="border border-black px-4 min-w-16 py-2"
                >
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ),
                  )}
                </select>
                <span className="text-sm text-gray-600">of {totalPages}</span>
              </div>
              <button
                type="button"
                className="border border-black px-4 py-2 disabled:opacity-50"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                Prev
              </button>
              <button
                type="button"
                className="border border-black px-4 py-2 disabled:opacity-50"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>

          <div className="results flex flex-wrap gap-12">
            {pagedResults.map((resource, index) => {
              const getImage = () => {
                if (resource?.type === "Word") {
                  return "/word.svg";
                }
                if (resource?.resource && resource?.resource.length > 0) {
                  return `${resource?.resource[0]?.cdn_url}?format=jpeg`;
                }
                return `${resource.documentLink}?format=jpeg`;
              };
              const getLink = () => {
                if (resource?.resource && resource?.resource.length > 0) {
                  return resource?.resource[0]?.cdn_url;
                }
                return resource?.documentLink;
              };
              return (
                <Card
                  key={`${resource.documentLink}-${index}`}
                  image={getImage()}
                  title={resource?.title}
                  line2={resource?.line2}
                  line3={resource?.line3}
                  description={""}
                  handle={getLink()}
                  download
                  tags={
                    resource?.contentfulMetadata?.tags?.map(
                      (tag: any) => tag.name,
                    ) as any
                  }
                  type={resource?.type}
                  thumbnail={getImage() as any}
                  index={index}
                />
              );
            })}
          </div>

          <If condition={totalPages > 1}>
            <div className="flex items-center justify-between pt-12">
              <div></div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Page</span>
                  <select
                    value={currentPage}
                    onChange={(e) => goToPage(Number(e.target.value))}
                    className="border border-black px-4 min-w-16 py-2"
                  >
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ),
                    )}
                  </select>
                  <span className="text-sm text-gray-600">of {totalPages}</span>
                </div>
                <button
                  type="button"
                  className="border border-black px-4 py-2 disabled:opacity-50"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="border border-black px-4 py-2 disabled:opacity-50"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </If>
        </If>
      </div>
    </div>
  );
}

const Details = ({
  title,
  children,
  open = true,
}: {
  title: string;
  children: React.ReactNode;
  open?: boolean;
}) => {
  const [currentOpen, setCurrentOpen] = useState(open);
  return (
    <Accordion.Root
      className="w-full"
      collapsible
      onValueChange={(value: string | undefined) => {
        setCurrentOpen(!!value);
      }}
      type="single"
      value={currentOpen ? title : ""}
    >
      <Accordion.Item
        value={title}
        className="border-b border-b-white px-4 w-full block"
      >
        <Accordion.Header>
          <Accordion.Trigger className={`stack gap-2 w-full pb-1`}>
            <div className="flex justify-between items-center w-full">
              <h2 className="font-bold uppercase">{title}</h2>
              <Plus
                className={`transtion-all ease-in-out duration-300 relative block ${
                  currentOpen ? "rotate-45" : ""
                }`}
                size={32}
                color="black"
                strokeWidth={0.5}
              />
            </div>
          </Accordion.Trigger>
        </Accordion.Header>

        <Accordion.Content
          className={`
                        overflow-hidden transition-all duration-300 ease-in-out
                        data-[state=open]:animate-slideDown
                        data-[state=closed]:animate-slideUp
                      `}
        >
          <div className="py-4">{children}</div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
